import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Box, Stack, SvgIcon, ThemeProvider, Typography } from '@mui/material';
import dayjs from 'dayjs';

import { usePaymentMethod, useUser } from '@mopla/business-logic';
import { EUROPE_BERLIN_TIMEZONE } from '@mopla/constants';
import { Logger } from '@mopla/logger';
import { passengerThemeV2 } from '@mopla/mui';
import {
	PaymentPortal,
	usePaymentPortalControls,
	useRestoreState,
} from '@mopla/payment-flow';
import type { AlternativePassenger } from '@mopla/ticketshop-api';
import {
	BillIcon,
	DTicketPageLayout,
	InformationIcon,
	MplCalendarSheet,
	MplContentLoader,
	ProfileOutlinedIcon,
	TicketDetailSection,
	ZoneIcon,
} from '@mopla/ui';
import ClockIcon from '@mopla/ui/icons/ClockIcon.svg';

import { usePassengerAppNavigate } from '../../../hooks';
import { useOperatorTickets } from '../operatorTickets/hooks';
import { useOperators } from '../ticketshop/hooks';

import { TicketHolderNameChangeContent } from './components/TicketHolderNameChangeContent';
import { TicketHolderNameCollapsedSummary } from './components/TicketHolderNameCollapsedSummary';
import {
	getPickerOpenDate,
	getValidityConfig,
} from './validity/validityLabels';
import {
	EntryStopContent,
	OperatorTicketDetailBottomSheet,
	OperatorTicketDetailSkeleton,
	StopSearchBottomSheet,
	UserInfoContent,
	ValidityCollapsedSummary,
	ValidityContent,
	ZonesCollapsedSummary,
	ZonesContent,
} from './components';
import { useOperatorDetail, useOperatorTicketDetail } from './hooks';
import {
	generateIdempotencyKey,
	getOperatorLegalInfo,
	PaymentFailureModal,
	PaymentMethodLabel,
	PaymentMethodRow,
	PaymentSuccessModal,
	PriceCalculation,
	type TBillingChoice,
	TosAcceptance,
	useCreatePurchase,
	YearlyPaymentOptions,
} from './payment';
import { resolvePriceTier } from './pricing';
import { isValidHolderName, normalizeHolderNamePart } from './ticketHolderName';
import { IStop } from './types';

type PurchaseResult = 'idle' | 'success' | 'failed';

/** State persisted across an external payment redirect (SEPA/PayPal). */
interface IRestoreState {
	selectedZoneIds: string[];
	selectedStop?: IStop;
	startDateOverride: string | null;
	operatorName?: string;
	tariffConditionsUrl?: string;
	priceGroupId?: string;
	billingChoice?: TBillingChoice;
	holderFirstNameOverride?: string | null;
	holderLastNameOverride?: string | null;
}

export const OperatorTicketDetail = () => {
	const { t } = useTranslation('ticketshop');
	const { operatorId, ticketId } = useParams<{
		operatorId: string;
		ticketId: string;
	}>();
	const location = useLocation();
	const [query] = useSearchParams();
	const {
		navigateBack,
		navigateToOperatorTickets,
		navigateToHome,
		navigateToMyTicketDetail,
	} = usePassengerAppNavigate();

	// Operator display meta arrives via router state, but it is lost after an
	// external payment redirect (SEPA/PayPal). Mirror it into state so it can be
	// re-hydrated from the persisted restore-state on return.
	const [operatorMeta, setOperatorMeta] = useState<{
		operatorName?: string;
		tariffConditionsUrl?: string;
		priceGroupId?: string;
	}>(() => {
		const s = (location.state ?? null) as {
			operatorName?: string;
			tariffConditionsUrl?: string;
			priceGroupId?: string;
		} | null;
		return {
			operatorName: s?.operatorName,
			tariffConditionsUrl: s?.tariffConditionsUrl,
			priceGroupId: s?.priceGroupId,
		};
	});

	// Replace on a deep link / web payment return, so OperatorTickets' own Back
	// doesn't pop back here.
	const handleBack = useCallback(
		() =>
			navigateBack(
				() =>
					operatorId && navigateToOperatorTickets(operatorId, { replace: true })
			),
		[navigateBack, navigateToOperatorTickets, operatorId]
	);

	const { ticketDetail, isLoading } = useOperatorTicketDetail(
		operatorId,
		ticketId
	);

	// Operator name / tariff-conditions URL derived from the (cached) catalog
	// queries keyed by the URL's operatorId — robust across refresh and external
	// payment redirects, where router state is gone. Router state (operatorMeta)
	// is the instant fallback on normal in-app navigation.
	const { operators } = useOperators();
	const { operatorTickets } = useOperatorTickets(operatorId);
	const operator = operators.find((o) => o.id === operatorId);
	const operatorName = operatorMeta.operatorName ?? operator?.name ?? '';
	// Operator logo (a bus for WendlandMobil) shown in the top section for every
	// ticket, mirroring Figma. Cached from the operators overview; refetched on a
	// deep-link / refresh.
	const operatorIconUrl = operator?.iconUrl;
	const tariffConditionsUrl =
		operatorMeta.tariffConditionsUrl ?? operatorTickets?.tariffConditionsUrl;

	const headerTitle = operatorName;

	const { userData } = useUser();

	const formattedDob = userData?.dateOfBirth
		? dayjs(userData.dateOfBirth).format('DD.MM.YYYY')
		: '';

	const firstName = userData?.firstName ?? '';
	const lastName = userData?.lastName ?? '';

	const ticketHolder = `${firstName} ${lastName} - ${formattedDob}`;

	const ticketName = ticketDetail?.name ?? '';

	const isStopBased = ticketDetail?.pricingModel === 'stop-based';

	const today = dayjs().tz(EUROPE_BERLIN_TIMEZONE).format('YYYY-MM-DD');

	const validityConfig = useMemo(
		() =>
			ticketDetail
				? getValidityConfig(
						ticketDetail.category,
						ticketDetail.validity,
						ticketDetail.startDates,
						today
				  )
				: null,
		[ticketDetail, today]
	);

	// User's explicit override (null → use the config default). Reset when the ticket changes.
	const [startDateOverride, setStartDateOverride] = useState<string | null>(
		null
	);
	const [lastTicketId, setLastTicketId] = useState(ticketId);
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	// ── Payment state ──────────────────────────────────────────────────────
	const [isAcceptTerms, setIsAcceptTerms] = useState(false);
	const [result, setResult] = useState<PurchaseResult>('idle');
	// Purchase id from the successful purchase response — used to open the new
	// ticket's detail page from the success modal.
	const [purchasedTicketId, setPurchasedTicketId] = useState<string | null>(
		null
	);
	const [isWaitingForPayment, setIsWaitingForPayment] = useState(false);
	const wasPaymentPortalOpenedRef = useRef(false);
	// One idempotency key per buy intent, reused verbatim on every retry (§10).
	const idempotencyKeyRef = useRef(generateIdempotencyKey());

	const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
	const [selectedStop, setSelectedStop] = useState<IStop | undefined>();
	const [isStopSearchOpen, setIsStopSearchOpen] = useState(false);
	// Billing choice for a "both" ticket (Jahresticket, §11). Monthly by default
	// (AC); ignored when the ticket offers a single mode (server fills it in).
	const [billingChoice, setBillingChoice] = useState<TBillingChoice>(
		'recurring_charge_per_period'
	);
	// Ticket holder name edits for a yearly ticket (null → prefilled user name).
	const [holderFirstNameOverride, setHolderFirstNameOverride] = useState<
		string | null
	>(null);
	const [holderLastNameOverride, setHolderLastNameOverride] = useState<
		string | null
	>(null);

	if (ticketId !== lastTicketId) {
		// React "reset state on prop change" pattern (no effect needed)
		setLastTicketId(ticketId);
		setStartDateOverride(null);
		setIsPickerOpen(false);
		setSelectedZoneIds([]);
		setSelectedStop(undefined);
		setIsAcceptTerms(false);
		setResult('idle');
		setPurchasedTicketId(null);
		setBillingChoice('recurring_charge_per_period');
		setHolderFirstNameOverride(null);
		setHolderLastNameOverride(null);
		idempotencyKeyRef.current = generateIdempotencyKey();
	}
	const selectedStartDate =
		startDateOverride ?? validityConfig?.defaultStartDate ?? null;

	const holderFirstName = holderFirstNameOverride ?? firstName;
	const holderLastName = holderLastNameOverride ?? lastName;

	type ValiditySectionId =
		| 'userInfo'
		| 'ticketHolderNameChange'
		| 'zones'
		| 'validity'
		| 'payment';
	const [openSection, setOpenSection] = useState<ValiditySectionId | null>(
		'zones'
	);
	const toggleSection = useCallback(
		(id: ValiditySectionId) =>
			setOpenSection((prev) => (prev === id ? null : id)),
		[]
	);

	const handleToggleZone = useCallback((zoneId: string) => {
		setSelectedZoneIds((prev) =>
			prev.includes(zoneId)
				? prev.filter((id) => id !== zoneId)
				: [...prev, zoneId]
		);
	}, []);

	// ── Payment method + portal + purchase ─────────────────────────────────
	const { defaultPaymentMethod, isPaymentMethodSaving, refetchPaymentMethod } =
		usePaymentMethod();
	const { openPaymentPortal, closePaymentPortal, isPaymentPortalOpened } =
		usePaymentPortalControls();
	const { purchase, isPurchasing } = useCreatePurchase();

	const hasPaymentMethod = !!defaultPaymentMethod;

	const { operatorDetail } = useOperatorDetail(operatorId);
	const legalInfo = useMemo(
		() =>
			getOperatorLegalInfo(
				operatorName,
				operatorDetail?.termsConditionsUrl,
				operatorDetail?.cancellationPolicyUrl
			),
		[operatorName, operatorDetail]
	);

	// Load the saved payment method on mount (the store is otherwise empty on a
	// fresh navigation), then reset the payment portal state when leaving.
	useEffect(() => {
		refetchPaymentMethod();
		return () => closePaymentPortal();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Persist the selection across an external payment redirect (SEPA/PayPal).
	const stateToRestore = useMemo<IRestoreState>(
		() => ({
			selectedZoneIds,
			selectedStop,
			startDateOverride,
			operatorName: operatorMeta.operatorName,
			tariffConditionsUrl: operatorMeta.tariffConditionsUrl,
			priceGroupId: operatorMeta.priceGroupId,
			billingChoice,
			holderFirstNameOverride,
			holderLastNameOverride,
		}),
		[
			selectedZoneIds,
			selectedStop,
			startDateOverride,
			operatorMeta,
			billingChoice,
			holderFirstNameOverride,
			holderLastNameOverride,
		]
	);
	useRestoreState({
		restoreState: stateToRestore,
		onStateRestored: (restored: IRestoreState) => {
			setSelectedZoneIds(restored.selectedZoneIds ?? []);
			setSelectedStop(restored.selectedStop);
			setStartDateOverride(restored.startDateOverride ?? null);
			// Re-hydrate the operator meta lost with the router state on redirect.
			setOperatorMeta({
				operatorName: restored.operatorName,
				tariffConditionsUrl: restored.tariffConditionsUrl,
				priceGroupId: restored.priceGroupId,
			});
			// Restore the monthly/one-time choice so the summary + total match what
			// the user picked before the Stripe redirect.
			if (restored.billingChoice) {
				setBillingChoice(restored.billingChoice);
			}
			setHolderFirstNameOverride(restored.holderFirstNameOverride ?? null);
			setHolderLastNameOverride(restored.holderLastNameOverride ?? null);
			setOpenSection('payment');
			setIsWaitingForPayment(true);
		},
	});

	const queryToRestore = useMemo(() => {
		const newQuery = new URLSearchParams(query);
		newQuery.set('isBankRedirect', '1');
		return newQuery.toString();
	}, [query]);

	const restorePage = `/ticketshop/${operatorId}/tickets/${ticketId}`;

	// Track portal open so we can detect its close.
	useEffect(() => {
		if (isPaymentPortalOpened) wasPaymentPortalOpenedRef.current = true;
	}, [isPaymentPortalOpened]);

	// On portal close, return to the detail view and confirm the method.
	useEffect(() => {
		if (!isPaymentPortalOpened && wasPaymentPortalOpenedRef.current) {
			wasPaymentPortalOpenedRef.current = false;
			setOpenSection('payment');
			refetchPaymentMethod();
			setIsWaitingForPayment(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isPaymentPortalOpened]);

	// Stop waiting once the method is confirmed; safety timeout for cancelled
	// external redirects (PayPal/SEPA) that never yield a method.
	useEffect(() => {
		if (!isWaitingForPayment) return;
		if (isPaymentMethodSaving) return;
		if (defaultPaymentMethod) {
			setIsWaitingForPayment(false);
			return;
		}
		const safetyTimer = setTimeout(() => setIsWaitingForPayment(false), 8000);
		return () => clearTimeout(safetyTimer);
	}, [isWaitingForPayment, isPaymentMethodSaving, defaultPaymentMethod]);

	// Price resolution — resolves the exact variant (§7b stop-based flat lookup,
	// §8 zone-based exact-or-network) plus the price shown to the user.
	const resolvedPrice = useMemo(
		() =>
			resolvePriceTier(
				ticketDetail,
				selectedZoneIds,
				operatorMeta.priceGroupId
			),
		[ticketDetail, selectedZoneIds, operatorMeta.priceGroupId]
	);
	const perPeriodPriceInCents = resolvedPrice.priceInCents;
	const variantId = resolvedPrice.tier?.variantId ?? '';

	// "both" tickets (Jahresticket) let the user pick monthly vs one-time
	// (MC-11411). Monthly bills the per-period rate; one-time bills it × periods.
	const offersBothBillingModes = ticketDetail?.billingMode === 'both';
	const periodCount = ticketDetail?.validity.periodCount ?? 1;
	const isOneTime = offersBothBillingModes && billingChoice === 'single_charge';
	const isYearlyTicket = ticketDetail?.category === 'subscription';
	// Send the holder name only when it differs from the user's own (prefilled) name.
	const normalizedHolderFirstName = normalizeHolderNamePart(holderFirstName);
	const normalizedHolderLastName = normalizeHolderNamePart(holderLastName);
	const isHolderNameChanged =
		normalizedHolderFirstName !== normalizeHolderNamePart(firstName) ||
		normalizedHolderLastName !== normalizeHolderNamePart(lastName);
	const alternativePassenger = useMemo<AlternativePassenger | undefined>(
		() =>
			isYearlyTicket && isHolderNameChanged
				? {
						firstName: normalizedHolderFirstName,
						lastName: normalizedHolderLastName,
				  }
				: undefined,
		[
			isYearlyTicket,
			isHolderNameChanged,
			normalizedHolderFirstName,
			normalizedHolderLastName,
		]
	);
	const totalPriceInCents =
		perPeriodPriceInCents === null
			? null
			: isOneTime
			? perPeriodPriceInCents * periodCount
			: perPeriodPriceInCents;

	// Regenerate the idempotency key whenever the buy intent changes (variant,
	// start date or entry stop). A key only dedupes retries of the SAME purchase;
	// reusing it for a changed selection makes the backend reject the new one with
	// 409 ("key reused with a different selection"). Retries of an unchanged
	// selection (e.g. after a 402 decline) intentionally keep the same key.
	const purchaseSignature = `${variantId}|${selectedStartDate ?? ''}|${
		selectedStop?.id ?? ''
	}|${offersBothBillingModes ? billingChoice : ''}|${
		alternativePassenger?.firstName ?? ''
	}|${alternativePassenger?.lastName ?? ''}`;
	const [lastPurchaseSignature, setLastPurchaseSignature] =
		useState(purchaseSignature);
	if (purchaseSignature !== lastPurchaseSignature) {
		setLastPurchaseSignature(purchaseSignature);
		idempotencyKeyRef.current = generateIdempotencyKey();
	}

	// Stop-based requires an entry stop; zone-based requires a resolvable price
	// (≥1 selected zone). Both need a purchasable variant. Yearly tickets also
	// need a valid ticket holder name.
	const isSelectionInvalid =
		(isStopBased
			? !selectedStop || !variantId
			: totalPriceInCents === null || !variantId || !selectedStartDate) ||
		(isYearlyTicket && !isValidHolderName(holderFirstName, holderLastName));

	const toggleTerms = useCallback(() => setIsAcceptTerms((p) => !p), []);

	// One-time ticket purchases are forced to Stripe; monthly/recurring
	// purchases keep the SEPA-or-Stripe choice (MC-11658). For "both" tickets
	// this follows the live billingChoice toggle, which can't change once the
	// portal (rendered as a full overlay) is open.
	const isSinglePaymentPurchase =
		ticketDetail?.billingMode === 'single_charge' || isOneTime;
	const handleOpenPaymentSelection = useCallback(
		() => openPaymentPortal(isSinglePaymentPurchase ? 'stripe' : undefined),
		[openPaymentPortal, isSinglePaymentPurchase]
	);
	// "Zur Zahlung" (no method, section collapsed) → reveal the Zahlung section.
	const handleExpandPayment = useCallback(() => setOpenSection('payment'), []);

	// Dismissing the portal (e.g. closing the SEPA info popup) must not trigger
	// the "waiting for payment" loader.
	const handleDismissPaymentPortal = useCallback(() => {
		wasPaymentPortalOpenedRef.current = false;
		closePaymentPortal();
	}, [closePaymentPortal]);

	const handlePurchase = useCallback(async () => {
		if (!ticketDetail || !variantId || isPurchasing) return;

		try {
			// short-trip: backend forces today, startDate is omitted (§10).
			const startDate =
				ticketDetail.category === 'short-trip'
					? undefined
					: selectedStartDate ?? undefined;

			const res = await purchase({
				ticketId: ticketDetail.ticketId,
				tariffId: ticketDetail.tariffId,
				variantId,
				idempotencyKey: idempotencyKeyRef.current,
				...(startDate ? { startDate } : {}),
				...(isStopBased
					? { stopId: selectedStop?.id }
					: { zoneIds: selectedZoneIds }),
				// Only "both" tickets require the mode; for single-mode tickets the
				// server fills it in and sending the other value is a 409 (§10).
				...(offersBothBillingModes ? { billingMode: billingChoice } : {}),
				...(alternativePassenger ? { alternativePassenger } : {}),
			});

			setPurchasedTicketId(res.purchaseId ?? null);
			setResult('success');
		} catch (e) {
			// Lazily resolve the logger — it is initialized during app bootstrap.
			Logger.getInstance('Ticketshop').error('[ticketshop] purchase failed', e);
			setResult('failed');
		}
	}, [
		ticketDetail,
		variantId,
		isPurchasing,
		selectedStartDate,
		purchase,
		isStopBased,
		selectedStop?.id,
		selectedZoneIds,
		offersBothBillingModes,
		billingChoice,
		alternativePassenger,
	]);

	// On a successful purchase, open the new ticket's detail page. `replace` drops
	// this purchase screen from history so Back from the ticket goes home (the
	// detail reads `fromPurchase` for that). Fall back home if the id is missing.
	const handlePurchaseSuccessConfirm = useCallback(() => {
		if (purchasedTicketId) {
			navigateToMyTicketDetail(purchasedTicketId, {
				replace: true,
				state: { fromPurchase: true },
			});
		} else {
			navigateToHome();
		}
	}, [purchasedTicketId, navigateToMyTicketDetail, navigateToHome]);

	// CTA state machine (mirrors the Figma section states):
	//  • section closed          → "Zur Zahlung" → expand the Zahlung section
	//  • section open, method set → "Jetzt kostenpflichtig kaufen" → purchase
	//  • section open, no method  → "Zahlungsmethode einrichten" → open chooser
	const isPaymentSectionOpen = openSection === 'payment';
	let ctaLabel: string;
	let handleCta: () => void;
	if (!isPaymentSectionOpen) {
		ctaLabel = t('ticket_detail.pay_cta');
		handleCta = handleExpandPayment;
	} else if (hasPaymentMethod) {
		ctaLabel = t('payment.buy_cta');
		handleCta = handlePurchase;
	} else {
		ctaLabel = t('payment.setup_payment_cta');
		handleCta = handleOpenPaymentSelection;
	}
	const ctaVariant: 'outlined' | 'contained' =
		isPaymentSectionOpen && hasPaymentMethod ? 'contained' : 'outlined';
	const ctaDisabled =
		isSelectionInvalid ||
		(isPaymentSectionOpen &&
			hasPaymentMethod &&
			(!isAcceptTerms || isPurchasing));

	const showContent = !isLoading && !!ticketDetail && !!validityConfig;

	return (
		<ThemeProvider theme={passengerThemeV2}>
			{isPaymentPortalOpened && (
				<PaymentPortal
					restorePage={restorePage}
					restoreQuery={queryToRestore}
					onDismiss={handleDismissPaymentPortal}
				/>
			)}

			<DTicketPageLayout
				title={headerTitle}
				isTitleLoading={isLoading && !headerTitle}
				onBack={handleBack}
				scrollable
				stickyHeader
				dataTestId="operator-ticket-detail-page"
			>
				{isWaitingForPayment ? (
					<MplContentLoader />
				) : isLoading ? (
					<OperatorTicketDetailSkeleton />
				) : (
					showContent &&
					ticketDetail &&
					validityConfig && (
						<Stack
							sx={{
								width: '100%',
								// Cancel DTicketPageLayout's 16px content margin-top so the
								// header→first-section gap is 32px (header pb + section pt),
								// matching Figma instead of 48px.
								mt: '-16px',
								// Reserve space at the bottom so the static bottom sheet
								// doesn't cover the last section while scrolling.
								pb: '160px',
							}}
						>
							<TicketDetailSection
								icon={
									operatorIconUrl ? (
										<Box
											component="img"
											src={operatorIconUrl}
											alt=""
											sx={{
												width: 48,
												height: 48,
												borderRadius: '24px',
												objectFit: 'cover',
												flexShrink: 0,
											}}
										/>
									) : (
										<Box sx={{ display: 'flex', color: 'primary.main' }}>
											<InformationIcon />
										</Box>
									)
								}
								title={ticketName}
								headerSubtitle={
									<Typography variant="l1" color="solutions.600">
										{t('ticket_detail.user_info.collapsed_subtitle')}
									</Typography>
								}
								open={openSection === 'userInfo'}
								onToggle={() => toggleSection('userInfo')}
								dataTestId="ticket-detail-user-info"
							>
								<UserInfoContent
									ticketHolder={ticketHolder}
									tariffConditionsUrl={tariffConditionsUrl}
									isYearlyTicket={isYearlyTicket}
								/>
							</TicketDetailSection>

							{/* Allow yearly ticket purchase for other users */}
							{isYearlyTicket && (
								<TicketDetailSection
									icon={<ProfileOutlinedIcon />}
									title={t(
										'ticket_detail.name_change_section.yearly_ticket_holder'
									)}
									withTopDivider
									defaultOpen
									collapsedSubtitle={
										<TicketHolderNameCollapsedSummary
											firstName={holderFirstName}
											lastName={holderLastName}
										/>
									}
									dataTestId="ticket-detail-user-name-change"
								>
									<TicketHolderNameChangeContent
										firstName={holderFirstName}
										lastName={holderLastName}
										onFirstNameChange={setHolderFirstNameOverride}
										onLastNameChange={setHolderLastNameOverride}
									/>
								</TicketDetailSection>
							)}

							{isStopBased ? (
								<TicketDetailSection
									icon={<ZoneIcon />}
									title={t('ticket_detail.stop_section_title')}
									withTopDivider
									defaultOpen={!isYearlyTicket}
									disableToggle
									dataTestId="ticket-detail-entry-stop"
								>
									<EntryStopContent
										stop={selectedStop}
										onClick={() => setIsStopSearchOpen(true)}
									/>
								</TicketDetailSection>
							) : (
								<TicketDetailSection
									icon={<ZoneIcon />}
									title={t('ticket_detail.zones_title')}
									withTopDivider
									defaultOpen={!isYearlyTicket}
									collapsedSubtitle={
										<ZonesCollapsedSummary
											zones={ticketDetail.zones}
											selectedZoneIds={selectedZoneIds}
										/>
									}
									dataTestId="ticket-detail-zones"
								>
									<ZonesContent
										zones={ticketDetail.zones}
										selectedZoneIds={selectedZoneIds}
										onToggleZone={handleToggleZone}
										zoneMapUrl={ticketDetail.zoneMapUrl}
									/>
								</TicketDetailSection>
							)}

							<TicketDetailSection
								icon={
									<SvgIcon
										component={ClockIcon}
										color="primary"
										sx={{ fontSize: 24 }}
									/>
								}
								title={t('ticket_detail.validity_title')}
								withTopDivider
								{...(isStopBased
									? { defaultOpen: true, disableToggle: true }
									: {
											open: openSection === 'validity',
											onToggle: () => toggleSection('validity'),
									  })}
								collapsedSubtitle={
									<ValidityCollapsedSummary
										config={validityConfig}
										selectedStartDate={selectedStartDate}
										validity={ticketDetail.validity}
									/>
								}
								dataTestId="ticket-detail-validity"
							>
								<ValidityContent
									config={validityConfig}
									validity={ticketDetail.validity}
									selectedStartDate={selectedStartDate}
									onSelectStartDate={setStartDateOverride}
									onOpenPicker={() => setIsPickerOpen(true)}
								/>
							</TicketDetailSection>

							<TicketDetailSection
								icon={<BillIcon />}
								title={t('ticket_detail.payment_title')}
								withTopDivider
								open={openSection === 'payment'}
								onToggle={() => toggleSection('payment')}
								collapsedSubtitle={
									hasPaymentMethod ? (
										<PaymentMethodLabel />
									) : (
										<Typography variant="l1" color="solutions.600">
											{t('ticket_detail.payment_no_method')}
										</Typography>
									)
								}
								dataTestId="ticket-detail-payment"
							>
								<PaymentMethodRow
									hasPaymentMethod={hasPaymentMethod}
									onEdit={handleOpenPaymentSelection}
								/>
								{perPeriodPriceInCents !== null &&
									(offersBothBillingModes ? (
										<YearlyPaymentOptions
											operatorName={operatorName}
											ticketName={ticketName}
											perPeriodPriceInCents={perPeriodPriceInCents}
											periodCount={periodCount}
											startDate={selectedStartDate ?? today}
											value={billingChoice}
											onChange={setBillingChoice}
										/>
									) : (
										<PriceCalculation
											operatorName={operatorName}
											ticketName={ticketName}
											priceInCents={perPeriodPriceInCents}
											category={ticketDetail.category}
											periodCount={ticketDetail.validity.periodCount}
											startDate={selectedStartDate ?? today}
										/>
									))}
								<TosAcceptance
									interactive={hasPaymentMethod}
									checked={isAcceptTerms}
									onToggle={toggleTerms}
									legalInfo={legalInfo}
								/>
							</TicketDetailSection>
						</Stack>
					)
				)}
			</DTicketPageLayout>

			{showContent && !isWaitingForPayment && result === 'idle' && (
				<OperatorTicketDetailBottomSheet
					totalPriceInCents={totalPriceInCents ?? 0}
					onContinue={handleCta}
					label={ctaLabel}
					variant={ctaVariant}
					isDisabled={ctaDisabled}
					isLoading={isPurchasing}
					subscriptionPeriodCount={
						// Monthly subscriptions (monthly-only, or a "both" ticket with the
						// monthly option chosen) show "{count} × {price}/Monat"; one-time
						// shows a flat total (MC-11411).
						isYearlyTicket && !isOneTime ? periodCount : undefined
					}
				/>
			)}

			{isStopBased && (
				<StopSearchBottomSheet
					open={isStopSearchOpen}
					operatorId={operatorId}
					selectedStop={selectedStop}
					onClose={() => setIsStopSearchOpen(false)}
					onSelect={setSelectedStop}
				/>
			)}

			{showContent && validityConfig?.picker && (
				<MplCalendarSheet
					open={isPickerOpen}
					value={getPickerOpenDate(validityConfig, selectedStartDate)}
					today={today}
					minDate={validityConfig.picker.minDate}
					maxDate={validityConfig.picker.maxDate}
					isSelectable={validityConfig.picker.isSelectable}
					getHighlightRange={validityConfig.picker.getHighlightRange}
					onConfirm={(iso) => {
						setStartDateOverride(iso);
						setIsPickerOpen(false);
					}}
					onCancel={() => setIsPickerOpen(false)}
					labels={{
						cancel: t('ticket_detail.validity_picker_cancel'),
						confirm: t('ticket_detail.validity_picker_confirm'),
					}}
				/>
			)}

			<PaymentSuccessModal
				open={result === 'success'}
				onConfirm={handlePurchaseSuccessConfirm}
			/>
			<PaymentFailureModal
				open={result === 'failed'}
				onClose={() => setResult('idle')}
			/>
		</ThemeProvider>
	);
};
